import moment from 'moment-timezone';

import {ChatType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ChatFileUpload, UserContext} from 'llm/chat-library/ChatLibraryTypes';
import {AgentMode} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import * as DataCaptureDAO from 'llm/data-capture/DataCaptureDAO';
import * as DeepResearchDAO from 'llm/deep-research/DeepResearchDAO';
import SidepanelStore from 'nav/sidepanel/SidepanelStore';
import WorkspaceStore from 'sidebar/WorkspaceStore';
import {assertNever} from 'util/Types';

/**
 * Creates a chat if it doesn't exist, and then post a message to the chat.
 *
 * This is an aspirational DAO, but currently our different agent implementations are vastly different,
 * where this intends to provide a unified interface that papers over the differences.
 *
 * Notably:
 *   - ASK / DATA_CAPTURE need a chat to exist before posting a message, while DEEP_RESEARCH does not.
 *   - DEEP_RESEARCH will quickly post a message synchronously and resposne happens async via celery.
 *   - DATA_CAPTURE will post a message asynchronously in celery.
 *   - ASK will post and often repond to a message synchronously, where the async task will no-op.
 *
 * For the best UX, caller should use optimistic rendering of the posted message, and then show loading state
 * while polling for the response.

* @param existingChatId The chat to post to. If `null`, a new chat will be created.
 * @param agentMode - each
 * @param message - components of a DisplayMessageChatEvent (DisplayTextBlock / DisplayFileBlock).
 * @param options - publicDataEnabled - only relevant for DEEP_RESEARCH.
 * @returns Promise that resolves as soon as the chat and initial message to backend has been initiated.
 *   As soon as a statusUrl is available, there may be persisted data that can be rendered. The response
 *   will definitely be available once the task associated with the statusUrl has completed.
 */
export async function sendMessage(
  existingChatId: string | null,
  agentMode: AgentMode,
  message: {
    text: string;
    fileUploads: Array<ChatFileUpload>;
  },
  options: {publicDataEnabled: boolean}
): Promise<{chatId: string; type: ChatType; statusUrl: Promise<string>}> {
  switch (agentMode) {
    case AgentMode.DEEP_RESEARCH: {
      const {chatId, statusUrl} = await DeepResearchDAO.initiateDeepResearchMessage(
        existingChatId ?? undefined /* chatId */,
        message.text /* message */,
        options.publicDataEnabled /* publicDataEnabled */,
        message.fileUploads /* fileUploads */
      );
      return {chatId, type: ChatType.DEEP_RESEARCH, statusUrl: Promise.resolve(statusUrl)};
    }
    case AgentMode.ASK: {
      const chatId = existingChatId ?? (await DeepResearchDAO.createChatSession());

      const activeIds = [];
      if (!SidepanelStore.getIsExpanded() && WorkspaceStore.activeId) {
        activeIds.push(WorkspaceStore.activeId);
      }

      // Capture user context for ASK mode
      const context: UserContext = {
        ids: activeIds,
        // intentionally en-US to match the rest of our prompting
        userLocalTime: new Date().toLocaleString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
        }),
        userTimezone: moment.tz.guess(),
      };

      const statusUrlPromise = DeepResearchDAO.initiateAskMessage(
        chatId,
        message.text,
        context,
        message.fileUploads
      ).then(({statusUrl}) => statusUrl);
      return {chatId, type: ChatType.DEEP_RESEARCH, statusUrl: statusUrlPromise};
    }
    case AgentMode.DATA_CAPTURE: {
      const chatId = existingChatId ?? (await DataCaptureDAO.createDataCaptureAssistantChat()).chatId;
      const statusUrlPromise = DataCaptureDAO.runDataCaptureAssistantWithoutPolling(
        chatId,
        message.text,
        message.fileUploads
      ).then((rv) => rv.status_url);
      return {chatId, type: ChatType.DATA_CAPTURE, statusUrl: statusUrlPromise};
    }
    default:
      throw new Error(`Unknown agent mode: ${assertNever(agentMode)}`);
  }
}

/**
 * Get the download URL for a file uploaded to a chat session.
 */
export function getChatFileDownloadUrl(chatSessionId: string, externalFileId: string): string {
  return `/1/api/chats/${chatSessionId}/uploaded-files/${externalFileId}:download`;
}

/**
 * Get the preview URL for a file uploaded to a chat session.
 */
export function getChatFilePreviewUrl(chatSessionId: string, externalFileId: string): string {
  return `/1/api/chats/${chatSessionId}/uploaded-files/${externalFileId}:preview`;
}
